import React, { forwardRef } from "react";
import { Button, ButtonProps } from "./Button";

export interface LinkButtonProps
  extends ButtonProps,
    Omit<React.AnchorHTMLAttributes<HTMLAnchorElement>, keyof ButtonProps> {}

/**
 * A Button component that renders as an anchor element for navigation.
 * Inherits all Button styling and behavior while providing link functionality.
 * Supports icons (iconPath, iconName, iconLocal) and busy states just like Button.
 *
 * All anchor element attributes (href, target, rel, etc.) are forwarded to the underlying <a> element.
 * Button-specific props like variant, color, disabled, busy, iconPath, etc. control the styling and behavior.
 */
export const LinkButton = forwardRef<HTMLAnchorElement, LinkButtonProps>(
  (
    {
      className,
      variant,
      color,
      disabled,
      muted,
      centered,
      dense,
      loading,
      render,
      renderSymbol,
      symbolLabel,
      iconPath,
      iconName,
      iconLocal,
      busy,
      children,
      ...anchorProps
    },
    ref
  ) => {
    return (
      <Button
        {...{
          className,
          variant,
          color,
          disabled,
          muted,
          centered,
          dense,
          loading,
          renderSymbol,
          symbolLabel,
          iconPath,
          iconName,
          iconLocal,
          busy,
        }}
        render={
          render ||
          (({ className, children: innerChildren, onClick }) => (
            <a
              ref={ref}
              className={className}
              onClick={onClick}
              {...anchorProps}
            >
              {innerChildren}
            </a>
          ))
        }
      >
        {children}
      </Button>
    );
  }
);

LinkButton.displayName = "LinkButton";
